import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@c2fo/liquidity';
import { ChevronDownIcon } from '@c2fo/liquidity/icons';
import emptystatements from '@/enterprise/assets/emptystatementsgrey.svg?url';
import { useReporting } from '@/reporting';
import { ButtonLink } from '@/shared/components/ButtonLink';
import Skeleton from '../Skeleton';

interface WidgetProps {
  items: { key: string; content: React.ReactNode; link: string }[];
  subtitle: string;
  title: string;
  viewAllLink: string;
  widgetType: 'offer-activity' | 'invoice-central' | 'early-pay-history';
  zeroItemsMessage: string;
  showViewAllWhenEmpty?: boolean;
}

export const WidgetSkeleton = () => {
  return (
    <div className="flex h-[408px] flex-col rounded-2xl bg-white shadow-md">
      <div className="space-y-1.5 px-4 py-6">
        <Skeleton className="m-auto h-8 w-5/6" />
        <Skeleton className="h-5" />
      </div>
      <div className="flex-1 overflow-hidden">
        {Array.from({ length: 3 }).map((_, idx) => (
          <div key={idx} className="space-y-1.5 border-t border-[rgba(57,57,57,0.12)] p-4 last-of-type:border-b">
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-10" />
            <Skeleton className="h-5 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );
};

const Widget = ({
  items,
  subtitle,
  title,
  viewAllLink,
  widgetType,
  zeroItemsMessage,
  showViewAllWhenEmpty,
}: WidgetProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleItemClick = () => {
    trackEnterpriseEvent('widget::item::clicked', {
      widgetType,
    });
  };

  const handleViewAllClick = () => {
    trackEnterpriseEvent('widget::view-all::clicked', {
      widgetType,
    });
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;

    if (!container) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;

    // show top shadow when scrolled down from the top
    setShowTopShadow(scrollTop > 0);
    // show bottom shadow when there's more content below
    setShowBottomShadow(scrollTop + clientHeight < scrollHeight);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;

    if (!container) {
      return;
    }

    // initial check for bottom shadow
    const { scrollHeight, clientHeight } = container;
    setShowBottomShadow(scrollHeight > clientHeight);

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="flex h-[408px] flex-col rounded-2xl bg-white shadow-md">
      <div className={cn('px-4 py-6 text-center', { 'border-b border-[rgba(57,57,57,0.12)]': items.length === 0 })}>
        <div className="text-2xl">{title}</div>
        <div className="text-sm text-text-secondary">{subtitle}</div>
      </div>
      {items.length > 0 ? (
        <div className="relative flex-1 overflow-hidden">
          {/* top shadow */}
          <div
            className={cn(
              `absolute inset-x-0 top-0 z-10 h-4 bg-gradient-to-b from-black/10 to-transparent opacity-0 transition-opacity duration-200`,
              {
                'opacity-100': showTopShadow,
              }
            )}
          />
          {/* bottom shadow */}
          <div
            className={cn(
              `absolute inset-x-0 bottom-0 z-10 h-4 bg-gradient-to-t from-black/10 to-transparent opacity-0 transition-opacity duration-200`,
              {
                'opacity-100': showBottomShadow,
              }
            )}
          />
          <div className="h-full overflow-y-auto" ref={scrollContainerRef}>
            {items.map((item) => (
              <Link
                key={item.key}
                className="group block border-t border-[rgba(57,57,57,0.12)] p-4 transition-all duration-200 last-of-type:border-b hover:bg-deepBlue-50"
                to={item.link}
                onClick={handleItemClick}
              >
                <div className="flex items-center">
                  <div className="mr-4 min-w-0 flex-1 overflow-hidden truncate">{item.content}</div>
                  <ChevronDownIcon className="size-6 shrink-0 -rotate-90 fill-secondary-500" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="-mt-5 flex h-full flex-col items-center justify-center p-6">
          <img src={emptystatements} alt="emptystatements" className="size-28" />
          <div className="text-center text-sm text-text-secondary">{zeroItemsMessage}</div>
        </div>
      )}
      {(items.length > 0 || showViewAllWhenEmpty) && (
        <div className="mt-auto p-5 text-center">
          <ButtonLink full to={viewAllLink} variant="secondary" onClick={handleViewAllClick}>
            {t('core.viewAll')}
          </ButtonLink>
        </div>
      )}
    </div>
  );
};

export default Widget;
